import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Table, type TableProps } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table/interface";
import { useTranslation } from "react-i18next";
import { DownArrowIcon } from "@/components/icons";
import { buildOrderChannelRows } from "@/utils/orderV2";
import { useIsMobileMd } from "@/hooks/useIsMobileMd";

type Props = Omit<TableProps<any>, "expandable" | "rowKey" | "rowClassName"> & {
  channelCards?: boolean;
  buildChannelRows?: (order: any) => any[];
  expandedOrderKeys: React.Key[];
  onExpandedOrderKeysChange: (keys: React.Key[]) => void;
};

function ChannelNameCell({ isChannel, children }: { isChannel: boolean; children: React.ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const connectorRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (isChannel) return;
    const content = contentRef.current;
    const cell = content?.closest("td");
    const avatar = content?.querySelector("[data-channel-avatar]");
    if (!content || !cell || !avatar) return;
    const positionConnector = () => {
      const cellRect = cell.getBoundingClientRect();
      const avatarRect = avatar.getBoundingClientRect();
      if (connectorRef.current) {
        connectorRef.current.style.top = `${avatarRect.bottom - cellRect.top}px`;
        connectorRef.current.style.left = `${avatarRect.left + avatarRect.width / 2 - cellRect.left}px`;
      }
    };
    positionConnector();
    const observer = new ResizeObserver(positionConnector);
    observer.observe(content);
    observer.observe(cell);
    observer.observe(avatar);
    return () => observer.disconnect();
  }, [isChannel]);
  return (
    <>
      <span ref={connectorRef} className="order-channel-connector" aria-hidden="true">
        <span className="order-channel-connector-dot" />
      </span>
      <div ref={contentRef} className="order-channel-name-content">{children}</div>
    </>
  );
}

// Tree rows share the parent's colgroup, fixed cells and horizontal scroller.
export default function CreatorChannelTable({
  dataSource,
  channelCards = false,
  buildChannelRows = buildOrderChannelRows,
  columns,
  rowSelection,
  expandedOrderKeys,
  onExpandedOrderKeysChange,
  ...tableProps
}: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobileMd();
  const reduceMotion = useReducedMotion();
  const [retainedExpandedKeys, setRetainedExpandedKeys] = useState(expandedOrderKeys);
  // Keep closing tree rows mounted until their cells have finished collapsing.
  useEffect(() => {
    if (!channelCards || reduceMotion) {
      setRetainedExpandedKeys(expandedOrderKeys);
      return;
    }
    setRetainedExpandedKeys((previous) => Array.from(new Set([...previous, ...expandedOrderKeys])));
    const timer = window.setTimeout(() => setRetainedExpandedKeys(expandedOrderKeys), 240);
    return () => window.clearTimeout(timer);
  }, [expandedOrderKeys, reduceMotion, channelCards]);
  const visibleExpandedKeys = !channelCards || reduceMotion
    ? expandedOrderKeys
    : Array.from(new Set([...retainedExpandedKeys, ...expandedOrderKeys]));
  const rows = useMemo(
    () =>
      dataSource?.map((order) => {
        const channels = buildChannelRows(order);
        return {
          ...order,
          __channelRowKey: order.id,
          __channelChildren: channels.length > 1
            ? channels.map((channel, index) => ({
                ...channel,
                __channelRowKey: JSON.stringify([
                  order.id,
                  "channel",
                  channel.channelKey,
                ]),
                __channelRowType: "channel",
                __channelParentKey: order.id,
                __channelFirst: index === 0,
                __channelLast: index === channels.length - 1,
              }))
            : undefined,
        };
      }),
    [dataSource, buildChannelRows],
  );
  const hasExpandableRows = rows?.some((row) => row.__channelChildren?.length);

  const sharedColumns = useMemo<ColumnsType<any>>(
    () => {
      const contentColumns = ((columns ?? []) as ColumnType<any>[]).filter(
        (column) => !column.hidden && column.dataIndex !== "isStar" && column.title !== "Actions",
      );
      const lastContentColumn = contentColumns.at(-1);
      return [
        ...(rowSelection ? [Table.SELECTION_COLUMN] : []),
        ...(hasExpandableRows && (!rowSelection || channelCards)
            ? [{
                key: "__channelExpand",
                width: 32,
                fixed: isMobile ? undefined : "left" as const,
                className: "order-channel-expand-cell",
                render: () => null,
              }]
            : []),
        ...((columns ?? []) as ColumnType<any>[]).map((column) => ({
          ...column,
          fixed: isMobile ? undefined : column.fixed,
          className: channelCards ? [
            column.className,
            column.dataIndex === "creatorName" ? "order-channel-name-cell" : "",
            column === lastContentColumn ? "order-channel-card-end" : "",
            column.dataIndex === "isStar" || column.title === "Actions" ? "order-channel-empty-cell" : "",
          ].filter(Boolean).join(" ") : column.className,
          render: (value: any, record: any, index: number) => {
            const isChannel = record.__channelRowType === "channel";
            const isOpen = expandedOrderKeys.includes(record.__channelParentKey);
            if (isChannel && (column.dataIndex === "isStar" || column.title === "Actions")) {
              return null;
            }
            const content = column.render ? column.render(value, record, index) : value;
            if (!channelCards) return content;
            const cellContent = isChannel ? (
              <motion.div
                className="order-channel-cell-collapse"
                initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                animate={{
                  height: isOpen ? "auto" : 0,
                  opacity: isOpen ? 1 : 0,
                  overflow: "hidden",
                  transitionEnd: { overflow: isOpen ? "visible" : "hidden" },
                }}
                transition={{ duration: reduceMotion ? 0 : 0.22, ease: [0.4, 0, 0.2, 1] }}
                aria-hidden={!isOpen}
                {...(!isOpen ? { inert: "" } : {})}
              >
                <div className="order-channel-cell-content"><div>{content}</div></div>
              </motion.div>
            ) : content;
            if (column.dataIndex === "creatorName" && (record.__channelRowType || record.__channelChildren?.length)) {
              return (
                <ChannelNameCell isChannel={isChannel}>{cellContent}</ChannelNameCell>
              );
            }
            return cellContent;
          },
        })),
      ];
    },
    [columns, rowSelection, isMobile, hasExpandableRows, expandedOrderKeys, reduceMotion, channelCards],
  );

  return (
    <Table
      {...tableProps}
      className={[tableProps.className, channelCards ? "creator-channel-cards" : ""].filter(Boolean).join(" ")}
      rowKey="__channelRowKey"
      columns={sharedColumns}
      dataSource={rows}
      rowClassName={(record) =>
        !channelCards
          ? record.__channelRowType ? `order-channel-row order-channel-${record.__channelRowType}` : ""
          : record.__channelRowType
          ? `order-channel-row order-channel-card-row order-channel-${record.__channelRowType}${record.__channelFirst ? " order-channel-first" : ""}${record.__channelLast ? " order-channel-last" : ""}${expandedOrderKeys.includes(record.__channelParentKey) ? "" : " order-channel-closing"}`
          : record.__channelChildren?.length && visibleExpandedKeys.includes(record.__channelRowKey)
            ? `order-channel-parent-expanded${expandedOrderKeys.includes(record.__channelRowKey) ? "" : " order-channel-parent-closing"}`
            : ""
      }
      rowSelection={
        rowSelection
          ? {
              ...rowSelection,
              columnWidth: hasExpandableRows && !channelCards
                ? Math.max(Number(rowSelection.columnWidth) || 0, 64)
                : rowSelection.columnWidth,
              columnTitle: hasExpandableRows && !channelCards
                ? (node) => (
                    <span className="order-channel-selection-title">
                      {typeof rowSelection.columnTitle === "function"
                        ? rowSelection.columnTitle(node)
                        : rowSelection.columnTitle ?? node}
                    </span>
                  )
                : rowSelection.columnTitle,
              fixed: isMobile ? false : rowSelection.fixed,
              checkStrictly: true,
              getCheckboxProps: (record) =>
                record.__channelRowType
                  ? { disabled: true }
                  : (rowSelection.getCheckboxProps?.(record) ?? {}),
              renderCell: (checked, record, index, node) =>
                record.__channelRowType
                  ? null
                  : rowSelection.renderCell
                    ? rowSelection.renderCell(checked, record, index, node)
                    : node,
            }
          : undefined
      }
      expandable={{
        childrenColumnName: "__channelChildren",
        expandedRowKeys: visibleExpandedKeys,
        onExpandedRowsChange: (keys) =>
          onExpandedOrderKeysChange(Array.from(keys)),
        // Ant Design places tree icons after selection when the index is omitted.
        expandIconColumnIndex: channelCards && rowSelection ? undefined : 0,
        indentSize: 0,
        expandIcon: ({ expandable, record, onExpand }) => {
          const expanded = expandedOrderKeys.includes(record.__channelRowKey);
          return (
          expandable ? (
            <button
              type="button"
              className={`order-channel-expand-button${rowSelection && !channelCards ? " order-channel-selection-arrow" : ""}`}
              aria-expanded={expanded}
              aria-label={t(
                expanded
                  ? "campaignDetail.creatorList.collapseChannels"
                  : "campaignDetail.creatorList.expandChannels",
              )}
              onClick={(event) => {
                event.stopPropagation();
                if (!channelCards) {
                  onExpand(record, event);
                  return;
                }
                onExpandedOrderKeysChange(expanded
                  ? expandedOrderKeys.filter((key) => key !== record.__channelRowKey)
                  : [...expandedOrderKeys, record.__channelRowKey]);
              }}
            >
              <DownArrowIcon
                className={expanded ? "" : "order-channel-arrow-collapsed"}
              />
            </button>
          ) : !channelCards && rowSelection && hasExpandableRows && !record.__channelRowType ? (
            <span
              aria-hidden="true"
              className="order-channel-selection-spacer"
            />
          ) : null
          );
        },
      }}
    />
  );
}
